import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GraphQLClient } from "graphql-request";
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  getDay,
  isSameDay,
  isSameMonth,
  startOfMonth,
  subMonths,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
} from "date-fns";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import { tokens } from "../../theme/tokens";
import { graphqlEndpoint } from "../../config";
import { useResolvedTenantId } from "../../hooks/useResolvedTenantId";
import { READ_SCHEDULED_TASKS } from "../../graph/tasks/queries";

const client = new GraphQLClient(graphqlEndpoint);

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const toIsoDateTime = (date) => {
  const d = new Date(date);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
};

export default function CalendarView() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const { tenantId, tenantName } = useResolvedTenantId();

  const [visibleMonth, setVisibleMonth] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  const monthStart = useMemo(() => startOfMonth(visibleMonth), [visibleMonth]);
  const monthEnd = useMemo(() => endOfMonth(visibleMonth), [visibleMonth]);

  const calendarStart = useMemo(
    () => startOfWeek(monthStart, { weekStartsOn: 1 }),
    [monthStart]
  );

  const calendarEnd = useMemo(
    () => endOfWeek(monthEnd, { weekStartsOn: 1 }),
    [monthEnd]
  );

  const calendarDays = useMemo(() => {
    return eachDayOfInterval({
      start: calendarStart,
      end: calendarEnd,
    });
  }, [calendarStart, calendarEnd]);

  const fetchScheduledTasks = useCallback(async () => {
    if (!tenantId) {
      setTasks([]);
      return;
    }

    try {
      setLoading(true);

      const dueFrom = calendarStart.toISOString();
      const dueTo = addDays(calendarEnd, 1).toISOString();

      const { readAllTasks } = await client.request(READ_SCHEDULED_TASKS, {
        tenantId,
        dueFrom,
        dueTo,
      });

      setTasks(readAllTasks || []);
    } catch (err) {
      console.error("Failed to load scheduled tasks:", err);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, calendarStart, calendarEnd]);

  useEffect(() => {
    fetchScheduledTasks();
  }, [fetchScheduledTasks]);

  const tasksForDay = useCallback(
    (day) => {
      return tasks
        .filter((task) => task.dueDate && isSameDay(new Date(task.dueDate), day))
        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
    },
    [tasks]
  );

  const handleCreateTaskForDay = (day) => {
    navigate("/createTask", {
      state: {
        redirectPath: "/calendar",
        initialValues: {
          status: "SCHEDULED",
          dueDate: toIsoDateTime(day),
        },
      },
    });
  };

  const handleCreateTask = () => {
    navigate("/createTask", {
      state: {
        redirectPath: "/calendar",
        initialValues: {
          status: "SCHEDULED",
        },
      },
    });
  };

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="CALENDAR"
          subtitle="Select a tenant before viewing scheduled tasks"
        />

        <Typography mt={3}>
          Use the tenant selector in the top bar, then return here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
        mb={2}
      >
        <Header
          title="CALENDAR"
          subtitle={
            tenantName
              ? `Scheduled tasks for ${tenantName}`
              : "Scheduled tasks"
          }
        />

        <Box display="flex" gap={1} flexWrap="wrap">
          <ActionButton
            tone="teal"
            variant="outlined"
            onClick={() => setVisibleMonth((current) => subMonths(current, 1))}
          >
            Previous
          </ActionButton>

          <ActionButton tone="teal" variant="outlined" onClick={() => setVisibleMonth(new Date())}>
            Today
          </ActionButton>

          <ActionButton
            variant="outlined"
            tone="teal"
            onClick={() => setVisibleMonth((current) => addMonths(current, 1))}
          >
            Next
          </ActionButton>

          <ActionButton tone="gold" onClick={handleCreateTask}>
            + Task
          </ActionButton>
        </Box>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        gap={2}
        flexWrap="wrap"
      >
        <Typography variant="h4" fontWeight={700}>
          {format(visibleMonth, "MMMM yyyy")}
        </Typography>

        {loading && (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading scheduled tasks...</Typography>
          </Box>
        )}
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(7, minmax(0, 1fr))"
        gap={1}
        mb={1}
      >
        {WEEK_DAYS.map((day) => (
          <Typography
            key={day}
            align="center"
            variant="body2"
            fontWeight={700}
            sx={{ color: colors.grey[400] }}
          >
            {day}
          </Typography>
        ))}
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(7, minmax(0, 1fr))"
        gap={1}
      >
        {calendarDays.map((day) => {
          const dayTasks = tasksForDay(day);
          const isToday = isSameDay(day, new Date());
          const isCurrentMonth = isSameMonth(day, visibleMonth);
          const isWeekend = [0, 6].includes(getDay(day));

          return (
            <Paper
              key={day.toISOString()}
              elevation={isToday ? 4 : 1}
              onDoubleClick={() => handleCreateTaskForDay(day)}
              sx={{
                p: 1,
                minHeight: 150,
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                cursor: "default",
                border: isToday
                  ? `2px solid ${colors.scalpelTeal[400]}`
                  : `1px solid ${colors.surface.border}`,
                bgcolor: !isCurrentMonth
                  ? colors.background.default
                  : dayTasks.length === 0
                  ? colors.background.default
                  : "background.paper",
                opacity: !isCurrentMonth ? 0.45 : dayTasks.length === 0 ? 0.82 : 1,
                "&:hover": {
                  borderColor: colors.scalpelTeal[400],
                },
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <Typography
                  variant="body2"
                  fontWeight={isToday ? 800 : 700}
                  sx={{
                    color: isToday
                      ? colors.scalpelTeal[300]
                      : isWeekend
                      ? colors.grey[400]
                      : "text.primary",
                  }}
                >
                  {format(day, "d")}
                </Typography>

                {dayTasks.length > 0 && (
                  <Chip
                    size="small"
                    label={dayTasks.length}
                    sx={{
                      height: 20,
                      minWidth: 22,
                      fontWeight: 700,
                    }}
                  />
                )}
              </Box>

              {dayTasks.length === 0 ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.grey[500],
                    fontStyle: "italic",
                    mt: 0.5,
                  }}
                >
                  Double-click to add
                </Typography>
              ) : (
                <Box display="flex" flexDirection="column" gap={0.75}>
                  {dayTasks.slice(0, 4).map((task) => (
                    <Paper
                      key={task.id}
                      elevation={2}
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/tasks/edit/${task.id}`, {
                          state: { redirectPath: "/calendar" },
                        });
                      }}
                      sx={{
                        p: 0.75,
                        borderLeft: `4px solid ${colors.scalpelTeal[400]}`,
                        cursor: "pointer",
                        bgcolor: colors.surface.card,
                        "&:hover": {
                          boxShadow: 4,
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      <Typography
                        fontWeight={700}
                        fontSize="0.8rem"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {task.title}
                      </Typography>

                      {task.project?.title && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.grey[400],
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {task.project.title}
                        </Typography>
                      )}
                    </Paper>
                  ))}

                  {dayTasks.length > 4 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.scalpelTeal[300],
                        fontWeight: 700,
                      }}
                    >
                      +{dayTasks.length - 4} more
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}